import { useCallback, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/i18n';
import { useCardId } from '@trello/id-context';
import { LeaveBoardIcon } from '@trello/nachos/icons/leave-board';
import { QuickCardEditorTestIds } from '@trello/test-ids';

import { useChangeCardRoleMutation } from './ChangeCardRoleMutation.generated';
import { QuickCardEditorButton } from './QuickCardEditorButton';
import { useRefetchConvertCardRoleButtonQuery } from './useRefetchConvertCardRoleButtonQuery';

const format = forTemplate('quick_card_editor');

interface ConvertCardRoleButtonProps {
  onLoad: () => void;
  analyticsContainers: Parameters<
    typeof Analytics.sendClickedButtonEvent
  >[0]['containers'];
  isLegacyView?: boolean;
}

export const ConvertCardRoleButton = ({
  onLoad,
  analyticsContainers,
  isLegacyView = false,
}: ConvertCardRoleButtonProps) => {
  const cardId = useCardId();
  const [changeCardRole] = useChangeCardRoleMutation();

  // Because fetching possibleCardRole is done on a separate
  // endpoint from other fields on the card, we must refetch
  // the value whenever the card model is changed in order to
  // ensure that we don't have stale data.
  const { data, loading } = useRefetchConvertCardRoleButtonQuery(cardId);

  // This is used to recalculate the height of the quick edit
  // menu after the button gets rendered. This will likely
  // need to be here until the quick edit menu gets rewritten
  // in the new stack, since as of now, this button gets rendered
  // *after* the menu is opened. Without this code, there is
  // a chance that the ConvertCardRoleButton will be off screen
  // and unable to be seen or clicked on
  useEffect(onLoad, [onLoad, data]);

  const cardRole = data?.card?.cardRole;
  const possibleCardRole = data?.card?.possibleCardRole;

  const canBeSeparatorCard = possibleCardRole === 'separator';
  const canBeBoardCard = possibleCardRole === 'board';
  const canBeLinkCard = possibleCardRole === 'link';

  const trackClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'convertCardButton',
      source: 'quickCardEditorInlineDialog',
      attributes: {
        fromCardRole: cardRole,
        toCardRole: cardRole ? null : possibleCardRole,
      },
      containers: analyticsContainers,
    });
  }, [analyticsContainers, cardRole, possibleCardRole]);

  const trackConversion = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (fromCardRole: any, toCardRole: any) => {
      Analytics.sendTrackEvent({
        action: 'converted',
        actionSubject: 'card',
        source: 'quickCardEditorInlineDialog',
        attributes: {
          fromCardRole,
          toCardRole,
        },
        containers: analyticsContainers,
      });
    },
    [analyticsContainers],
  );

  const convertCard = useCallback(() => {
    trackClick();

    if (cardRole) {
      changeCardRole({
        variables: {
          cardId,
          cardRole: null,
        },
      }).then(() => trackConversion(cardRole, null));
    } else if (canBeBoardCard) {
      changeCardRole({
        variables: {
          cardId,
          cardRole: 'board',
        },
      }).then(() => trackConversion(null, 'board'));
    } else if (canBeSeparatorCard) {
      changeCardRole({
        variables: {
          cardId,
          cardRole: 'separator',
        },
      }).then(() => trackConversion(null, 'separator'));
    } else if (canBeLinkCard) {
      changeCardRole({
        variables: {
          cardId,
          cardRole: 'link',
        },
      }).then(() => trackConversion(null, 'link'));
    }
  }, [
    trackClick,
    cardRole,
    canBeBoardCard,
    canBeSeparatorCard,
    canBeLinkCard,
    changeCardRole,
    cardId,
    trackConversion,
  ]);

  if (loading || (!cardRole && !possibleCardRole)) {
    return null;
  }

  return (
    <QuickCardEditorButton
      icon={<LeaveBoardIcon color="currentColor" size="small" />}
      onClick={convertCard}
      testId={QuickCardEditorTestIds.ConvertRole}
      isLegacyView={isLegacyView}
    >
      {!!cardRole && format('convert-to-regular-card')}
      {!cardRole && canBeBoardCard && format('convert-to-board-card')}
      {!cardRole && canBeSeparatorCard && format('convert-to-separator-card')}
      {!cardRole && canBeLinkCard && format('convert-to-link-card')}
    </QuickCardEditorButton>
  );
};
